/**
 * Copy mirrors the real LEGAL_BUSINESS_NAME and SELECT_NET_TERMS signup steps:
 * packages/signup/signup/src/views/SignUp/legal-business-name/LegalBusinessNameStep.tsx
 * packages/signup/signup/src/views/SignUp/select-payment-terms/__internal__/Headers.tsx
 */
export const LEGAL_BUSINESS_NAME_COPY = {
  title: "What's your store's legal business name?",
  subtitle:
    "We use this info to verify retailers. Sharing it now helps us process your orders more quickly.",
  fieldLabel: "Legal business name",
  ctaLabel: "Next",
} as const;

export const NET_TERMS_COPY = {
  title: "Congrats! You've got payment terms",
  subtitle:
    "This means you can buy inventory today and pay up to 60 days later—interest-free.",
  ctaLabel: "Start buying",
} as const;

export const NET_TERMS_CARD_COPY = {
  availableTermsLabel: "Your available payment terms",
} as const;

export const NET_TERMS_DISCLAIMER =
  "We use manual and automated processes to verify all retailers on Faire.";

export const RETAILER_STORE_NAME = "Corner Market Co.";
export const NET_TERMS_AMOUNT_USD = 500;

/**
 * Real context, not a hypothesis: submitting Legal Business Name kicks off
 * setPaymentTerm, which typically resolves in ~2s. Today that gap is covered
 * by nothing more than a spinner on the Next button — every variant below
 * is paced close to that same window, not stretched out artificially.
 */
export const TRANSITION_CONTEXT_NOTE = {
  eyebrow: "Why this exists",
  title: "The real wait is ~2 seconds",
  body: "Submitting Legal Business Name kicks off a real API call — verifying the business and calculating payment terms — that typically resolves in about 2 seconds. Today that gap is covered by nothing more than a spinner on the Next button. Each variant below is paced close to that same ~2–4s window (see durations per style) so the wait doesn't get longer, just more purposeful to sit through.",
} as const;

export type TransitionVariantKey =
  | "spinner-subtitles"
  | "progress-checklist"
  | "anticipation-build"
  | "minimal-brand";

export interface TransitionVariantConfig {
  key: TransitionVariantKey;
  label: string;
  hypothesis: string;
  durationMs: number;
}

export const TRANSITION_VARIANTS: TransitionVariantConfig[] = [
  {
    key: "spinner-subtitles",
    label: "Spinner + rotating subtitles",
    hypothesis:
      "Same pattern as Brand Portal's web-bridge “finishing up” modal: a static title with subtitles that rotate every few seconds. Something new to read during a wait that today shows only a button spinner should reduce perceived wait time, at low build cost.",
    durationMs: 4200,
  },
  {
    key: "progress-checklist",
    label: "Progress checklist",
    hypothesis:
      "Concrete steps (verifying business, checking eligibility, calculating terms) complete one at a time instead of an ambiguous spinner. Naming what's happening for a financial decision should build more trust than motion alone.",
    durationMs: 3900,
  },
  {
    key: "anticipation-build",
    label: "Anticipation build",
    hypothesis:
      "The headline itself progresses (“Verifying…” → “Almost there…” → “Calculating your terms…”), so the wait reads as a countdown to a reward. Should make the confetti reveal that follows feel more earned.",
    durationMs: 3600,
  },
  {
    key: "minimal-brand",
    label: "Minimal brand moment",
    hypothesis:
      "A restrained version — logo mark pulsing with one line of copy, no rotating trivia. Tests whether a subdued tone builds more trust than a playful one for a moment tied directly to money and credit.",
    durationMs: 2600,
  },
];

export const SPINNER_ROTATING_SUBTITLES = [
  "Verifying your business details…",
  "Checking your eligibility…",
  "Calculating your payment terms…",
  "Almost done…",
] as const;

export const PROGRESS_CHECKLIST_STEPS = [
  "Verifying your business",
  "Checking your eligibility",
  "Calculating your terms",
] as const;

export const ANTICIPATION_BUILD_PHASES = [
  "Verifying your business…",
  "Almost there…",
  "Calculating your payment terms…",
] as const;

export const MINIMAL_BRAND_MESSAGE = "Setting up your account…" as const;

export type CelebrationVariantKey =
  | "classic-rain"
  | "corner-burst-countup"
  | "money-confetti"
  | "milestone-card";

export interface CelebrationVariantConfig {
  key: CelebrationVariantKey;
  label: string;
  hypothesis: string;
}

export const CELEBRATION_VARIANTS: CelebrationVariantConfig[] = [
  {
    key: "classic-rain",
    label: "Classic confetti rain",
    hypothesis:
      "Baseline: generic brand-colored confetti falling from the top, the same treatment used at the very end of signup today. Establishes whether celebrating this specific moment — not just the final welcome — moves the needle at all.",
  },
  {
    key: "corner-burst-countup",
    label: "Corner burst + count-up amount",
    hypothesis:
      "Confetti bursts from all four corners in sync with the dollar figure counting up from $0 to the retailer's actual terms limit. Watching your own number animate in should feel more personal than the amount just appearing as static text.",
  },
  {
    key: "money-confetti",
    label: "Money-themed confetti",
    hypothesis:
      "Confetti recolored into bill-green and coin-gold instead of generic brand colors, tying the celebration visually to what was just unlocked. Thematic confetti should read as more intentional than brand confetti reused from elsewhere in the app.",
  },
  {
    key: "milestone-card",
    label: "Milestone card",
    hypothesis:
      "The terms amount appears inside a tangible card artifact (like a credit card) with a light shimmer sweep, instead of confetti over the page. Tests whether a physical-metaphor artifact feels more like a real financial credential than a purely decorative animation.",
  },
];
